import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Palmtree, Clock, CheckCircle2, XCircle, Ban, AlertCircle } from "lucide-react";
import { formatDateES } from "@/lib/dateUtils";
import FileUploadZone from "@/components/FileUploadZone";
import { getRequestableTypes } from "@/lib/portalVisibility";

const STATUS_MAP = {
  // Iter 188 (P0) — Flujo simplificado
  pendiente_jefe: { label: "Pendiente jefe directo", class: "bg-amber-100 text-amber-700", icon: Clock },
  pendiente_gerente: { label: "Pendiente gerente", class: "bg-amber-200 text-amber-800", icon: Clock },
  aprobada: { label: "Aprobada", class: "bg-green-100 text-green-700", icon: CheckCircle2 },
  rechazada: { label: "Rechazada", class: "bg-red-100 text-red-700", icon: XCircle },
  cancelada_empleado: { label: "Cancelada por empleado", class: "bg-slate-100 text-slate-500", icon: Ban },
  comunicada: { label: "Comunicada", class: "bg-purple-100 text-purple-700", icon: AlertCircle },
  documentacion_pendiente: { label: "Doc. Pendiente", class: "bg-orange-100 text-orange-700", icon: AlertCircle },
  // Legacy fallbacks
  en_aprobacion: { label: "En aprobacion (legacy)", class: "bg-amber-100 text-amber-700", icon: Clock },
  cancelada: { label: "Cancelada (legacy)", class: "bg-slate-100 text-slate-500", icon: Ban },
  registrada: { label: "Registrada (legacy)", class: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  cerrada: { label: "Archivada", class: "bg-slate-200 text-slate-600", icon: CheckCircle2 },
};

export default function VacationsPage() {
  const { axiosAuth, user } = useAuth();
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [hrConfig, setHrConfig] = useState(null);
  const [form, setForm] = useState({ request_type: "vacaciones", start_date: "", end_date: "", comments: "" });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Iter 234 (P1) — Mostrar saldo del empleado en su propia vista
  const [myBalance, setMyBalance] = useState(null);
  const isAdmin = ["admin", "super_admin", "jefe_taller", "jefe_recambios"].includes(user?.role);

  const fetchVacations = useCallback(async () => {
    try {
      const res = await axiosAuth().get("/vacation-requests");
      setVacations(res.data);
    } catch {
      toast.error("Error al cargar solicitudes");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  const loadConfig = useCallback(async () => {
    try {
      const endpoint = isAdmin ? "/hr/config" : "/hr/employee-config";
      const res = await axiosAuth().get(endpoint);
      setHrConfig(res.data);
    } catch {}
  }, [axiosAuth, isAdmin]);

  // Iter 234 (P1) — Carga el saldo personal del empleado para mostrarlo arriba.
  const loadMyBalance = useCallback(async () => {
    if (isAdmin) return;
    try {
      const res = await axiosAuth().get("/hr/my-balances");
      setMyBalance(res.data || null);
    } catch { /* silencio: empleado sin ficha o sin saldos */ }
  }, [axiosAuth, isAdmin]);

  useEffect(() => { fetchVacations(); loadConfig(); loadMyBalance(); }, [fetchVacations, loadConfig, loadMyBalance]);

  // Tipos que el usuario puede SOLICITAR. Aplica `portal_visibility.allow_request`.
  // OCULTAR TARJETA EN DASHBOARD NO IMPIDE SOLICITAR. Fuente única: lib/portalVisibility.js
  const activeTypes = getRequestableTypes(hrConfig);
  const selectedType = activeTypes.find(t => t.value === form.request_type);

  const handleCreate = async () => {
    if (!form.request_type) { toast.error("Selecciona un tipo de solicitud"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Selecciona las fechas de inicio y fin"); return; }
    if (form.end_date < form.start_date) { toast.error("La fecha fin debe ser posterior a la de inicio"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, has_initial_docs: pendingFiles.length > 0 };
      const res = await axiosAuth().post("/vacation-requests", payload);
      const reqId = res.data?.id;

      let uploadErrors = 0;
      if (reqId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            await axiosAuth().post(`/vacation-requests/${reqId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch { uploadErrors++; }
        }
      }

      if (uploadErrors > 0) {
        toast.warning(`Solicitud creada. ${uploadErrors} archivo(s) no se pudieron subir.`);
      } else if (pendingFiles.length > 0) {
        toast.success(`Solicitud enviada con ${pendingFiles.length} documento(s)`);
      } else {
        toast.success("Solicitud enviada correctamente");
      }
      setShowDialog(false);
      setForm({ request_type: activeTypes[0]?.value || "vacaciones", start_date: "", end_date: "", comments: "" });
      setPendingFiles([]);
      fetchVacations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar solicitud");
    } finally { setSubmitting(false); }
  };

  const cancelReq = async (id) => {
    try {
      await axiosAuth().put(`/vacation-requests/${id}/cancel`);
      toast.success("Solicitud cancelada");
      fetchVacations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cancelar");
    }
  };

  return (
    <div className="space-y-6" data-testid="vacations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase" data-testid="vacations-title">
            {isAdmin ? "Vacaciones y Permisos" : "Mis Vacaciones"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{vacations.length} solicitudes</p>
        </div>
        {!isAdmin && (
          <Button onClick={() => { setForm({ request_type: activeTypes[0]?.value || "vacaciones", start_date: "", end_date: "", comments: "" }); setShowDialog(true); }}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-9 px-5"
            data-testid="new-vacation-btn">
            <Plus className="w-4 h-4 mr-2" /> Solicitar
          </Button>
        )}
      </div>

      {/* Iter 234 (P1) — Tarjeta saldo del empleado (solo vista personal) */}
      {!isAdmin && myBalance && myBalance.vacaciones && (
        <Card className="border-slate-200 rounded-sm" data-testid="my-balance-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Palmtree className="w-4 h-4 text-slate-600" />
              <h2 className="text-xs uppercase tracking-wider font-semibold text-slate-700">Mi saldo de vacaciones — {new Date().getFullYear()}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(() => {
                const v = myBalance.vacaciones || {};
                const assigned = v.assigned || 0;
                const approved = (v.approved_future || 0) + (v.used_past || 0);
                const pending = v.pending || 0;
                const available = assigned - approved - pending;
                const band = available <= 0 ? "bg-slate-100 text-slate-600"
                  : available <= 3 ? "bg-red-100 text-red-700"
                  : available <= 10 ? "bg-amber-100 text-amber-700"
                  : "bg-green-100 text-green-700";
                return (
                  <>
                    <div className="p-3 bg-slate-50 rounded-sm">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Asignados</div>
                      <div className="text-2xl font-bold text-slate-900" data-testid="my-balance-assigned">{assigned}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-sm">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Disfrutados / aprobados</div>
                      <div className="text-2xl font-bold text-slate-900" data-testid="my-balance-approved">{approved}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-sm">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Pendientes</div>
                      <div className="text-2xl font-bold text-slate-900" data-testid="my-balance-pending">{pending}</div>
                    </div>
                    <div className={`p-3 rounded-sm ${band}`}>
                      <div className="text-[10px] uppercase tracking-wider opacity-80">Disponibles</div>
                      <div className="text-2xl font-bold" data-testid="my-balance-available">{available}</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Empleado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Desde</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Hasta</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Dias</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider font-semibold">Estado</TableHead>
                  {!isAdmin && <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
                ) : vacations.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    <Palmtree className="w-8 h-8 mx-auto mb-2 text-slate-300" />No hay solicitudes
                  </TableCell></TableRow>
                ) : (
                  vacations.map((v) => {
                    const st = STATUS_MAP[v.status] || { label: v.status, class: "bg-slate-100" };
                    return (
                      <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`vacation-row-${v.id}`}>
                        <TableCell className="text-sm font-medium text-slate-900">{v.employee_name}</TableCell>
                        <TableCell className="text-sm text-slate-700 capitalize">{v.request_type?.replace(/_/g, " ")}</TableCell>
                        <TableCell className="text-sm font-mono text-slate-700">{formatDateES(v.start_date)}</TableCell>
                        <TableCell className="text-sm font-mono text-slate-700">{formatDateES(v.end_date)}</TableCell>
                        <TableCell className="text-sm text-slate-700">{v.working_days || "-"}</TableCell>
                        <TableCell>
                          <Badge className={`rounded-sm text-[10px] ${st.class}`}>{st.label}</Badge>
                        </TableCell>
                        {!isAdmin && (
                          <TableCell className="text-right">
                            {/* Iter 188 (P0) — Cancelar disponible en cualquier estado pendiente */}
                            {["pendiente_jefe", "pendiente_gerente", "en_aprobacion"].includes(v.status) && (
                              <Button variant="ghost" size="sm" onClick={() => cancelReq(v.id)}
                                className="text-xs h-7 text-red-600 hover:text-red-700" data-testid={`cancel-vacation-${v.id}`}>Cancelar</Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md rounded-sm" data-testid="vacation-create-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Solicitar Ausencia</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Tipo</Label>
              <Select value={form.request_type} onValueChange={(val) => setForm({...form, request_type: val})}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="dialog-vac-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {activeTypes.length > 0 ? (
                    activeTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-xs text-slate-500" data-testid="dialog-vac-no-types">
                      No hay tipos disponibles. Contacta con RRHH.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Desde *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="dialog-vac-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Hasta *</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="dialog-vac-end" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Comentarios</Label>
              <Textarea value={form.comments} onChange={(e) => setForm({...form, comments: e.target.value})} className="rounded-sm border-slate-200" data-testid="dialog-vac-comments" />
            </div>
            {/* File Upload */}
            <FileUploadZone
              files={pendingFiles}
              setFiles={setPendingFiles}
              requirement={selectedType?.requires_docs ? "required" : "optional"}
              disabled={submitting}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); setPendingFiles([]); }} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={handleCreate} disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="dialog-vac-submit">
              {submitting ? "Enviando..." : pendingFiles.length > 0 ? `Enviar con ${pendingFiles.length} doc${pendingFiles.length > 1 ? "s" : ""}` : "Enviar solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
